import { Capacitor } from '@capacitor/core';

/**
 * Apple Pay / Google Pay through the native Stripe SDK, for the Capacitor builds.
 *
 * The web `ExpressCheckoutElement` cannot serve the app, and not because of anything we do wrong:
 * Stripe's own in-app-webview table says Google Pay in an Android webview needs *the host app* to
 * support the Payment Request API, which Capacitor does not, and closes the section recommending the
 * mobile SDKs instead. So the browser keeps `WalletExpressCheckout` and the app comes here.
 *
 * **Nothing in this module may be imported statically from `@capacitor-community/stripe`.** The
 * plugin ships a web implementation built on `stripe-pwa-elements` and `@stripe/stripe-js@^8` — peers
 * that do not match this app's React 19 and stripe-js 9 — and we never want it. Loading the plugin
 * through `import()` inside the native-only paths keeps it out of the browser bundle entirely.
 *
 * The two wallets are configured very differently, which is why so little is shared below:
 *
 * | | Android / Google Pay | iOS / Apple Pay |
 * |---|---|---|
 * | Key | `res/values/stripe.xml`, at build time | `Stripe.initialize()`, from the API |
 * | Merchant, country | same generated resources | passed per request, from `VITE_STRIPE_*` |
 * | Amount | taken from the intent | must be restated for the sheet |
 */

/** What the customer did with the native sheet. Cancelling is not a decline — see `CheckoutForm`. */
export type NativeWalletOutcome = 'completed' | 'canceled' | 'failed';

/**
 * Apple's merchant identifier, e.g. `merchant.com.amorim.jncquoi.app`.
 *
 * Build-time rather than server-supplied because it has to match the Xcode capability that was
 * compiled into the binary — a value fetched at runtime could disagree with the entitlement, and the
 * sheet would refuse to open with nothing explaining why.
 *
 * **Unset is the supported state.** Apple Pay needs the merchant ID, the Xcode capability and a
 * certificate issued through the Stripe dashboard, and until all three exist there is nothing for
 * this code to drive. Leaving it unset keeps iOS on the web element exactly as before, so this
 * module can ship ahead of the account work.
 */
function appleMerchantId(): string | undefined {
  return import.meta.env.VITE_STRIPE_APPLE_MERCHANT_ID?.trim() || undefined;
}

/** Country of the merchant account, ISO 3166-1 alpha-2. Mirrors the Android resource's default. */
function merchantCountry(): string {
  return import.meta.env.VITE_STRIPE_COUNTRY_CODE?.trim() || 'PT';
}

/** What the Apple Pay sheet calls the payee. Mirrors the Android resource's default. */
function merchantDisplayName(): string {
  return import.meta.env.VITE_STRIPE_MERCHANT_DISPLAY_NAME?.trim() || 'JNcQUOI';
}

/** Which native wallet a platform uses, or `null` where the web element serves instead. */
type NativeWalletKind = 'googlePay' | 'applePay';

/**
 * Which native wallet applies here, if any.
 *
 * iOS resolves to `null` while `VITE_STRIPE_APPLE_MERCHANT_ID` is unset. That is a deliberate
 * fallback rather than a failure: without the merchant ID `createApplePay` cannot be called at all,
 * so pretending the native path exists would replace a working web element with a dead row.
 */
function nativeWalletKind(): NativeWalletKind | null {
  if (!Capacitor.isNativePlatform()) return null;

  const platform = Capacitor.getPlatform();
  if (platform === 'android') return 'googlePay';
  if (platform === 'ios') return appleMerchantId() ? 'applePay' : null;
  return null;
}

/**
 * Whether the native wallet path applies at all.
 *
 * Kept as a boolean because that is all `CheckoutForm` gates on — which wallet it turns out to be is
 * this module's business, not the form's.
 */
export function isNativeWalletPlatform(): boolean {
  return nativeWalletKind() !== null;
}

/**
 * Warns when the key the app was built with is not the key the API is serving.
 *
 * The Android plugin reads its publishable key from `res/values/stripe.xml` at build time, not from
 * `GET /me/checkout/config`, so the same value now lives in two places and can drift — and the
 * symptom of drift is a sheet that refuses to open with nothing explaining why. We cannot read the
 * Android resource from JS, but the resource is generated from `VITE_STRIPE_PUBLISHABLE_KEY` by
 * `scripts/generate-native-stripe-config.ts`, so comparing against that env var tests the same thing.
 *
 * Android only: on iOS the plugin takes its key from `Stripe.initialize()`, so there is only ever one
 * value and nothing to drift against.
 */
function warnOnKeyDrift(apiPublishableKey: string): void {
  const buildKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
  if (!buildKey) {
    console.warn(
      'nativeWallet: VITE_STRIPE_PUBLISHABLE_KEY is unset, so the generated Android resources cannot ' +
        'hold a key either. Google Pay will not open. Set it and rebuild the native project.'
    );
    return;
  }
  if (buildKey !== apiPublishableKey) {
    console.warn(
      'nativeWallet: the build-time Stripe key does not match the one from /me/checkout/config ' +
        `(build ends ${buildKey.slice(-6)}, API ends ${apiPublishableKey.slice(-6)}). The native ` +
        'resources are generated from the build-time value, so Google Pay is talking to a different ' +
        'account or mode than the rest of the checkout.'
    );
  }
}

/**
 * Initialises the SDK and asks the device whether it can pay.
 *
 * Both availability checks **reject** when the wallet is unavailable rather than resolving `false`,
 * so the answer has to be caught, not tested. Returns a plain boolean because that is what the wallet
 * row is gated on, and the reason for a `false` belongs in the log rather than in the UI.
 */
export async function probeNativeWallet(publishableKey: string): Promise<boolean> {
  const kind = nativeWalletKind();
  if (!kind) return false;

  if (kind === 'googlePay') warnOnKeyDrift(publishableKey);

  try {
    const { Stripe } = await import('@capacitor-community/stripe');
    await Stripe.initialize({ publishableKey });

    if (kind === 'applePay') await Stripe.isApplePayAvailable();
    else await Stripe.isGooglePayAvailable();

    return true;
  } catch (error) {
    // Expected on an Android emulator with no Play Services, on any device with no card in its
    // wallet, and on an iOS simulator without a provisioned test card. Logged because "no wallet
    // row" is otherwise indistinguishable from a broken build.
    console.warn(`probeNativeWallet: ${kind} is not available on this device:`, error);
    return false;
  }
}

/** What the sheet is charging for. Apple Pay restates it; Google Pay reads it from the intent. */
export interface NativeWalletPayment {
  /** Client secret of an intent that already exists — the plugin has no deferred mode. */
  clientSecret: string;
  /**
   * Total in the currency's major unit, e.g. `10.99`.
   *
   * Apple Pay draws its own sheet from this rather than from the intent, so a wrong value here shows
   * the customer one figure and charges another. Google Pay ignores it.
   */
  amountMajor: number;
  /** ISO 4217, e.g. `EUR`. Apple Pay only. */
  currencyCode: string;
  /** Line-item label in the Apple Pay sheet. Defaults to the merchant name when omitted. */
  label?: string;
}

/**
 * Opens the native sheet for an intent that already exists.
 *
 * The client secret is required up front — the plugin has no deferred mode — which inverts the web
 * flow, where the intent is created *after* authorisation. That is the shape the server contract
 * always wanted (see `docs/api/endpoints-guide.md`, "Wallet clients should call `payment-intents`
 * before showing the wallet sheet"), and it is safe: only one payment can be in flight per purchase,
 * so an abandoned sheet leaves an intent that `payments/active` recovers and the next attempt reuses
 * rather than duplicates.
 *
 * Google Pay is handed nothing but the client secret. Every other field of `CreateGooglePayOption` is
 * marked "Web only" in the plugin's own types; on Android the merchant name, country and test-mode
 * flag come from the generated native resources, which is why they are build-time values.
 *
 * Apple Pay is the opposite: `CreateApplePayOption` requires the merchant identifier, country,
 * currency and the sheet's line items on every call, so they are assembled here.
 *
 * Throws are left to the caller — `CheckoutForm` already funnels every wallet failure into one
 * outcome, and swallowing here would cost that.
 */
export async function payWithNativeWallet({
  clientSecret,
  amountMajor,
  currencyCode,
  label,
}: NativeWalletPayment): Promise<NativeWalletOutcome> {
  const kind = nativeWalletKind();
  if (!kind) {
    console.warn('payWithNativeWallet: called off the native path — nothing can be presented here.');
    return 'failed';
  }

  const { Stripe, ApplePayEventsEnum, GooglePayEventsEnum } = await import('@capacitor-community/stripe');

  if (kind === 'applePay') {
    await Stripe.createApplePay({
      paymentIntentClientSecret: clientSecret,
      // Cannot be undefined here: `nativeWalletKind` only answers `applePay` when it is set, which
      // is the whole reason iOS stays on the web element until it is.
      merchantIdentifier: appleMerchantId()!,
      countryCode: merchantCountry(),
      currency: currencyCode,
      // One summary item, and Apple treats the last one as the total — so a single row both lists
      // and totals the purchase. Itemising would mean the rows had to sum exactly to the intent, and
      // the checkout's own summary already serves that purpose on the screen behind the sheet.
      paymentSummaryItems: [{ label: label ?? merchantDisplayName(), amount: amountMajor }],
    });

    const { paymentResult } = await Stripe.presentApplePay();

    if (paymentResult === ApplePayEventsEnum.Completed) return 'completed';
    if (paymentResult === ApplePayEventsEnum.Canceled) return 'canceled';
    return 'failed';
  }

  await Stripe.createGooglePay({ paymentIntentClientSecret: clientSecret });
  const { paymentResult } = await Stripe.presentGooglePay();

  if (paymentResult === GooglePayEventsEnum.Completed) return 'completed';
  if (paymentResult === GooglePayEventsEnum.Canceled) return 'canceled';
  return 'failed';
}
